import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  getIdrPrompt, saveIdrPrompt, getIdrPromptVersions, rollbackIdrPrompt, draftIdrSummary,
} from "../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   IDR LETTER WORDING

   The wording that drafts an IDR justification letter, edited one
   section at a time. The role statement and the JSON contract are not
   here: they stay in code so a wording edit cannot break drafting.

   Nothing saved here rewrites a letter that already exists. A save
   changes what the next draft says, and only that.
   ═══════════════════════════════════════════════════════════════════ */

const PANE_MODALITIES = "__modalities__";
const PANE_PROCEDURES = "__procedures__";

/* ─── icons ─────────────────────────────────────────────────────── */
const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>
    <path d="M4 6l4 4 4-4" />
  </svg>
);

/* ─── small shared bits ─────────────────────────────────────────── */
function Tag({ kind, children }) {
  const styles = {
    edited: { bg: "#d1fae5", fg: "#059669", bd: "#a7f3d0" },
    quiet: { bg: "var(--gray-100, #f3f4f6)", fg: "var(--text-tertiary)", bd: "var(--border)" },
    warn: { bg: "#fffbeb", fg: "#d97706", bd: "#fde68a" },
  }[kind] || { bg: "var(--blue-50, #eff6ff)", fg: "var(--blue-600, #1d4ed8)", bd: "var(--blue-100, #dbeafe)" };
  return (
    <span style={{
      fontSize: 9.5, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 6px",
      borderRadius: 4, background: styles.bg, color: styles.fg, border: `1px solid ${styles.bd}`,
      whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Field({ label, value, onChange, rows = 3, mono }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", textTransform: "uppercase", color: "var(--text-tertiary)", marginBottom: 4 }}>{label}</div>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
        style={{
          width: "100%", fontSize: 12, lineHeight: 1.6, padding: "8px 10px", borderRadius: 6,
          border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-primary)",
          fontFamily: mono ? "var(--font-mono)" : "inherit", resize: "vertical",
        }} />
    </label>
  );
}

/* ─── version history ───────────────────────────────────────────── */
function HistoryPanel({ onClose, onRolledBack, userName }) {
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getIdrPromptVersions()
      .then(r => setVersions(r.versions || []))
      .catch(e => setErr(e.message || "Could not load history"));
  }, []);

  async function restore(v) {
    if (!window.confirm(
      `Restore the wording saved ${new Date(v.lastModified).toLocaleString()} by ${v.modifiedBy}?\n\n` +
      "Letters already drafted are not changed. This only affects letters drafted from now on."
    )) return;
    setBusy(v.versionId);
    setErr("");
    try {
      await rollbackIdrPrompt(v.versionId, userName);
      onRolledBack();
    } catch (e) {
      setErr(e.message || "Rollback failed");
    } finally {
      setBusy("");
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Version history</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Restoring is forward only. Letters already drafted stay as they are.</div>
          </div>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>
        <div style={{ padding: 16, maxHeight: "60vh", overflowY: "auto" }}>
          {err && <div style={errBox}>{err}</div>}

        {meta.storedProblem && (
          <div style={{
            fontSize: 12, color: "#7c4a03", background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 7, padding: "10px 12px", marginBottom: 12,
          }}>
            <strong>Saved wording is not in use.</strong> {meta.storedProblem}
          </div>
        )}
          {versions === null && <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Loading...</div>}
          {versions?.length === 0 && <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>No saved versions yet. The shipped wording is in use.</div>}
          {versions?.map(v => (
            <div key={v.versionId} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              padding: "9px 0", borderBottom: "1px solid var(--border)",
            }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>
                  Version {v.promptVersion}
                  {v.isCurrentVersion && <span style={{ marginLeft: 8 }}><Tag>Current</Tag></span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  {v.lastModified ? new Date(v.lastModified).toLocaleString() : "—"} &middot; {v.modifiedBy}
                </div>
              </div>
              {!v.isCurrentVersion && (
                <button className="btn btn-secondary btn-sm" disabled={!!busy}
                  onClick={() => restore(v)} style={{ fontSize: 11 }}>
                  {busy === v.versionId ? "Restoring..." : "Restore"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── preview ───────────────────────────────────────────────────── */
function PreviewPanel({ config, dirty, onClose }) {
  const [pid, setPid] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  async function run(e) {
    e.preventDefault();
    const caseId = pid.trim();
    if (!caseId) return;
    setRunning(true);
    setErr("");
    setResult(null);
    try {
      // With unsaved edits, draft the case both ways so the effect of the
      // change can be read side by side. Otherwise one draft is enough.
      const [saved, pending] = await Promise.all([
        draftIdrSummary(caseId),
        dirty ? draftIdrSummary(caseId, config) : Promise.resolve(null),
      ]);
      setResult({ saved: saved.draft?.case_summary || "", pending: pending?.draft?.case_summary || "",
                  notes: (pending || saved).reviewNotes || [] });
    } catch (e2) {
      setErr(e2.message || "Preview failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: dirty ? 980 : 640 }} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Preview on a case</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
              Nothing is saved. A preview never reaches a generated letter.
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>

        <form onSubmit={run} style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          <input value={pid} onChange={e => setPid(e.target.value)} placeholder="Case ID (PID)"
            style={{ flex: 1, fontSize: 12, padding: "7px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-primary)" }} />
          <button className="btn btn-primary btn-sm" disabled={running || !pid.trim()} style={{ fontSize: 11 }}>
            {running ? "Drafting..." : "Draft"}
          </button>
        </form>

        <div style={{ padding: 16, maxHeight: "60vh", overflowY: "auto" }}>
          {err && <div style={errBox}>{err}</div>}

        {meta.storedProblem && (
          <div style={{
            fontSize: 12, color: "#7c4a03", background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 7, padding: "10px 12px", marginBottom: 12,
          }}>
            <strong>Saved wording is not in use.</strong> {meta.storedProblem}
          </div>
        )}
          {!result && !running && !err && (
            <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              Enter a case with a History and Physical. Only cases sent to arbitration are eligible for IDR.
            </div>
          )}
          {result && (
            <div style={{ display: "flex", gap: 14 }}>
              <div style={previewCol}>
                <div style={previewLabel}>{dirty ? "Saved wording" : "Current wording"}</div>
                <div style={letterStyle}>{result.saved || "—"}</div>
              </div>
              {dirty && (
                <div style={{ ...previewCol, borderColor: "var(--blue-500, #2563eb)" }}>
                  <div style={{ ...previewLabel, color: "var(--blue-600, #1d4ed8)" }}>With your edit</div>
                  <div style={letterStyle}>{result.pending || "—"}</div>
                </div>
              )}
            </div>
          )}
          {result?.notes?.length > 0 && (
            <div style={{ marginTop: 14, padding: "10px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 7 }}>
              <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", textTransform: "uppercase", color: "#d97706", marginBottom: 5 }}>Review notes</div>
              {result.notes.map((n, i) => (
                <div key={i} style={{ fontSize: 11.5, color: "#7c4a03", marginBottom: 2 }}>&bull; {n}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const BulletListIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round">
    <circle cx="2.5" cy="4" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="2.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="2.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <line x1="6" y1="4" x2="14" y2="4" />
    <line x1="6" y1="8" x2="14" y2="8" />
    <line x1="6" y1="12" x2="14" y2="12" />
  </svg>
);

const NumberListIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <text x="0.4" y="5.6" fontSize="5.2" fill="currentColor" stroke="none"
          fontFamily="var(--font-mono), monospace">1</text>
    <text x="0.4" y="10" fontSize="5.2" fill="currentColor" stroke="none"
          fontFamily="var(--font-mono), monospace">2</text>
    <text x="0.4" y="14.4" fontSize="5.2" fill="currentColor" stroke="none"
          fontFamily="var(--font-mono), monospace">3</text>
    <line x1="6.5" y1="4" x2="14" y2="4" />
    <line x1="6.5" y1="8" x2="14" y2="8" />
    <line x1="6.5" y1="12" x2="14" y2="12" />
  </svg>
);

/* ─── section editor, with markers the model can read ───────────── */
function MarkerToolbar({ textareaRef, value, onChange }) {
  // Work on whole lines: a marker halfway through a line means nothing.
  const apply = (fn) => {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const start = value.lastIndexOf("\n", a - 1) + 1;
    const endRaw = value.indexOf("\n", b);
    const end = endRaw === -1 ? value.length : endRaw;
    const block = value.slice(start, end);
    const next = block.split("\n").map(fn).join("\n");
    const updated = value.slice(0, start) + next + value.slice(end);
    onChange(updated);
    // Keep the caret on the same text after the markers shift it along.
    const delta = next.length - block.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + (a >= start ? delta : 0), b + delta);
    });
  };

  const bullet = () => apply(l => (/^\s*- /.test(l) ? l.replace(/^(\s*)- /, "$1") : l.replace(/^(\s*)/, "$1- ")));
  const number = () => {
    let n = 0;
    apply(l => {
      if (/^\s*\d+\. /.test(l)) return l.replace(/^(\s*)\d+\. /, "$1");
      n += 1;
      return l.replace(/^(\s*)/, `$1${n}. `);
    });
  };
  const btn = {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 26, height: 24, padding: 0, borderRadius: 5, cursor: "pointer",
    border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-secondary)",
  };

  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 6 }}>
      <button type="button" style={btn} onClick={bullet} title="Bullet the selected lines"
              aria-label="Bullet list">
        <BulletListIcon />
      </button>
      <button type="button" style={btn} onClick={number} title="Number the selected lines"
              aria-label="Numbered list">
        <NumberListIcon />
      </button>
      <span style={{ fontSize: 10.5, color: "var(--text-tertiary)", marginLeft: 4 }}>
        Model reads exactly what is typed.
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════════ */
export default function AdminIdrPrompt() {
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.name || accounts?.[0]?.username || "Admin";

  const [baseline, setBaseline] = useState(null);   // last loaded from the server
  const [config, setConfig] = useState(null);       // what is on screen
  const [meta, setMeta] = useState({});
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [openOverride, setOpenOverride] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [overrideSearch, setOverrideSearch] = useState("");
  const [openGroup, setOpenGroup] = useState("");
  // Remembered per procedure, so moving between them and back returns to
  // the example you were reading.
  const [openExample, setOpenExample] = useState({});
  const sectionRef = React.useRef(null);

  // quiet refetches leave the page on screen. Only the first load shows the
  // skeleton; swapping the whole page out mid-session reads as a reload.
  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    setErr("");
    try {
      const r = await getIdrPrompt();
      const next = {
        sections: r.sections || [],
        modalityWording: r.modalityWording || {},
        modalityAliases: r.modalityAliases || {},
        procedureLanguage: r.procedureLanguage || [],
        modalityLanguage: r.modalityLanguage || {},
      };
      setBaseline(JSON.parse(JSON.stringify(next)));
      setConfig(next);
      setMeta({ version: r.version, updatedAt: r.updatedAt, updatedBy: r.updatedBy,
                isDefault: r.isDefault, storedProblem: r.storedProblem || "" });
      setSelected(s => s || next.sections[0]?.key || "");
    } catch (e) {
      setErr(e.message || "Could not load the wording");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const dirty = useMemo(
    () => !!baseline && JSON.stringify(baseline) !== JSON.stringify(config),
    [baseline, config],
  );

  useEffect(() => { if (dirty) setJustSaved(false); }, [dirty]);

  // A procedure counts as written when any of its examples has text. Derived
  // once: the rail and the pane both read it, and when they each had their own
  // the rail kept counting a field that no longer existed.
  const procedureWritten = useMemo(
    () => (config?.procedureLanguage || [])
      .filter(p => (p.examples || []).some(e => (e.body || "").trim())).length,
    [config],
  );

  const needsWording = useMemo(
    () => Object.keys(config?.modalityWording || {}).filter(k => !config.modalityWording[k]).sort(),
    [config],
  );

  const changedKeys = useMemo(() => {
    if (!baseline || !config) return new Set();
    const out = new Set();
    config.sections.forEach(s => {
      const was = baseline.sections.find(b => b.key === s.key);
      if (!was || was.body !== s.body) out.add(s.key);
    });
    if (JSON.stringify(baseline.modalityWording) !== JSON.stringify(config.modalityWording)) out.add(PANE_MODALITIES);
    if (JSON.stringify(baseline.procedureLanguage) !== JSON.stringify(config.procedureLanguage)) out.add(PANE_PROCEDURES);
    if (JSON.stringify(baseline.modalityLanguage) !== JSON.stringify(config.modalityLanguage)) out.add(PANE_MODALITIES);
    return out;
  }, [baseline, config]);

  function setSectionBody(key, body) {
    setConfig(c => ({ ...c, sections: c.sections.map(s => (s.key === key ? { ...s, body } : s)) }));
  }

  function setModalityWording(recorded, phrasing) {
    setConfig(c => ({ ...c, modalityWording: { ...c.modalityWording, [recorded]: phrasing } }));
  }

  function editProcedure(key, fn) {
    setConfig(c => ({
      ...c,
      procedureLanguage: c.procedureLanguage.map(p => (p.key === key ? fn(p) : p)),
    }));
  }

  function addExample(key) {
    editProcedure(key, p => {
      const used = new Set((p.examples || []).map(e => e.key));
      let n = (p.examples || []).length + 1;
      while (used.has(`example_${n}`)) n += 1;
      const key2 = `example_${n}`;
      setOpenExample(m => ({ ...m, [key]: key2 }));
      return { ...p, examples: [...(p.examples || []), { key: key2, label: "", body: "" }] };
    });
  }

  function removeExample(key, exKey, label) {
    if (!window.confirm(
      `Remove ${label ? `"${label}"` : "this example"}?\n\nLetters already drafted are unchanged.`
    )) return;
    editProcedure(key, p => {
      const left = (p.examples || []).filter(e => e.key !== exKey);
      setOpenExample(m => ({ ...m, [key]: left.length ? left[0].key : "" }));
      return { ...p, examples: left };
    });
  }

  function setExample(key, exKey, field, value) {
    editProcedure(key, p => ({
      ...p,
      examples: (p.examples || []).map(e => (e.key === exKey ? { ...e, [field]: value } : e)),
    }));
  }

  function setProcedureLevel(key, level) {
    editProcedure(key, p => ({ ...p, level }));
  }

  function setModalityWording(recorded, phrasing) {
    setConfig(c => ({ ...c, modalityWording: { ...c.modalityWording, [recorded]: phrasing } }));
  }

  function setModalityLanguage(recorded, text) {
    setConfig(c => ({ ...c, modalityLanguage: { ...c.modalityLanguage, [recorded]: text } }));
  }

  async function handleSave() {
    setSaving(true);
    setErr("");
    try {
      const saved = await saveIdrPrompt(config, userName);
      // The save succeeded, so what is on screen is what is stored. Adopt it
      // as the new baseline instead of refetching: no refetch, no flash, and
      // the editor keeps its place.
      setBaseline(JSON.parse(JSON.stringify(config)));
      setMeta({
        version: saved.version,
        updatedAt: saved.updatedAt,
        updatedBy: saved.updatedBy,
        isDefault: false,
      });
      setJustSaved(true);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div className="page-body"><div className="skeleton" style={{ height: 400, borderRadius: 10 }} /></div>
  );

  if (!config) return (
    <div className="page-body"><div style={errBox}>{err || "Could not load the wording."}</div></div>
  );

  const section = config.sections.find(s => s.key === selected);

  return (
    <>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">IDR letter wording</div>
          <div className="page-subtitle">
            {meta.isDefault
              ? "Shipped wording · no edits saved yet"
              : `Version ${meta.version} · saved by ${meta.updatedBy || "Unknown"}` +
                (meta.updatedAt ? ` · ${new Date(meta.updatedAt).toLocaleString()}` : "")}
          </div>
        </div>
        <div className="header-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {dirty && (
            <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "#d97706", background: "#fffbeb", padding: "3px 10px", borderRadius: 5, border: "1px solid #fde68a" }}>
              Unsaved changes
            </span>
          )}
          {!dirty && justSaved && (
            <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "#059669", background: "#d1fae5", padding: "3px 10px", borderRadius: 5, border: "1px solid #a7f3d0" }}>
              Saved
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setShowHistory(true)} style={{ fontSize: 11 }}>
            Version history
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPreview(true)} style={{ fontSize: 11 }}>
            Preview on a case
          </button>
          {dirty && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfig(JSON.parse(JSON.stringify(baseline)))} style={{ fontSize: 11 }}>
                Discard
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ fontSize: 11 }}>
                {saving ? "Saving..." : "Save changes"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="page-body">
        {err && <div style={errBox}>{err}</div>}

        {meta.storedProblem && (
          <div style={{
            fontSize: 12, color: "#7c4a03", background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 7, padding: "10px 12px", marginBottom: 12,
          }}>
            <strong>Saved wording is not in use.</strong> {meta.storedProblem}
          </div>
        )}

        <div style={{
          fontSize: 11.5, color: "var(--text-secondary)", background: "var(--gray-50, #f9fafb)",
          border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", marginBottom: 14,
        }}>
          Saving changes what the next letter says. Letters already drafted are never rewritten, and every
          save is versioned and attributed.
        </div>

        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {/* Rail */}
          <div className="card" style={{ width: 300, flex: "none", overflow: "hidden" }}>
            <div style={railHead}>Sections of the letter</div>
            {config.sections.map(s => (
              <button key={s.key} onClick={() => setSelected(s.key)} style={railRow(selected === s.key)}>
                <span style={{ textAlign: "left" }}>{s.label}</span>
                {changedKeys.has(s.key) && <Tag kind="edited">Edited</Tag>}
              </button>
            ))}
            <div style={railHead}>Shared wording</div>
            <button onClick={() => setSelected(PANE_MODALITIES)} style={railRow(selected === PANE_MODALITIES)}>
              <span style={{ textAlign: "left" }}>Modality wording</span>
              {changedKeys.has(PANE_MODALITIES)
                ? <Tag kind="edited">Edited</Tag>
                : needsWording.length > 0
                  ? <Tag kind="warn">{needsWording.length} to fill</Tag>
                  : <Tag kind="quiet">{Object.keys(config.modalityWording || {}).length}</Tag>}
            </button>
            <button onClick={() => setSelected(PANE_PROCEDURES)} style={railRow(selected === PANE_PROCEDURES)}>
              <span style={{ textAlign: "left" }}>Procedure language</span>
              {changedKeys.has(PANE_PROCEDURES)
                ? <Tag kind="edited">Edited</Tag>
                : <Tag kind="quiet">{procedureWritten}/{(config.procedureLanguage || []).length}</Tag>}
            </button>
          </div>

          {/* Pane */}
          <div className="card" style={{ flex: 1, padding: "16px 18px", minWidth: 0 }}>
            {section && (
              <>
                <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 3 }}>{section.label}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 10 }}>{section.hint}</div>
                <MarkerToolbar textareaRef={sectionRef} value={section.body}
                               onChange={v => setSectionBody(section.key, v)} />
                <textarea
                  ref={sectionRef}
                  value={section.body}
                  onChange={e => setSectionBody(section.key, e.target.value)}
                  rows={18}
                  style={{
                    width: "100%", fontSize: 12.5, lineHeight: 1.65, padding: "11px 13px", borderRadius: 7,
                    border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-primary)",
                    resize: "vertical",
                  }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--text-tertiary)" }}>
                  <span>{changedKeys.has(section.key) ? "1 unsaved change in this section" : "No unsaved changes"}</span>
                  <span>Heading in the prompt: {section.heading}</span>
                </div>
              </>
            )}

            {selected === PANE_MODALITIES && (
              <>
                <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 3 }}>Modality wording</div>
                <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 10 }}>
                  Keyed on the modality label rather than the recorded value, so Upper MEP and Lower MEP
                  are both MEP: the letter names it once and the rationale is written once. A recorded
                  value with no label is written into the letter exactly as recorded.
                </div>

                {needsWording.length > 0 && (
                  <div style={{
                    fontSize: 11.5, color: "#7c4a03", background: "#fffbeb", border: "1px solid #fde68a",
                    borderRadius: 7, padding: "9px 12px", marginBottom: 11,
                  }}>
                    {needsWording.join(", ")} {needsWording.length === 1 ? "has" : "have"} no name yet, so
                    {needsWording.length === 1 ? " it is" : " they are"} left out of every letter and raised
                    as a review note. Fill the name in to start using
                    {needsWording.length === 1 ? " it" : " them"}.
                  </div>
                )}

                <div className="card" style={{ overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table-fixed" style={{ minWidth: 620 }}>
                      <thead>
                        <tr>
                          <th style={{ width: "17%" }}>Label</th>
                          <th style={{ width: "36%" }} title="What paragraph 2 calls it">Name in the letter</th>
                          <th style={{ width: "47%" }} title="Why it was used; drawn on only for cases that ran it">Language</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(config.modalityWording || {})
                          .sort((a, b) => {
                            // Anything still unnamed sits at the top: it is the
                            // only row that changes what a letter leaves out.
                            const ae = !config.modalityWording[a];
                            const be = !config.modalityWording[b];
                            return ae === be ? a.localeCompare(b) : (ae ? -1 : 1);
                          })
                          .map(label => {
                            const empty = !config.modalityWording[label];
                            return (
                              <tr key={label} style={{ background: empty ? "#fffbeb" : undefined }}>
                                <td style={{
                                  fontSize: 11.5, fontFamily: "var(--font-mono)", fontWeight: 600,
                                  color: "var(--text-secondary)", verticalAlign: "top", paddingTop: 12,
                                }}>{label}</td>
                                <td style={{ verticalAlign: "top" }}>
                                  <input
                                    value={config.modalityWording[label]}
                                    placeholder={empty ? "Needed; left out until set" : ""}
                                    onChange={e => setModalityWording(label, e.target.value)}
                                    style={{
                                      width: "100%", fontSize: 12, padding: "6px 9px", borderRadius: 5,
                                      border: `1px solid ${empty ? "#fde68a" : "var(--border)"}`,
                                      background: "var(--white)", color: "var(--text-primary)",
                                    }} />
                                </td>
                                <td style={{ verticalAlign: "top" }}>
                                  <textarea
                                    value={(config.modalityLanguage || {})[label] || ""}
                                    rows={2}
                                    placeholder="Optional. Why this modality was used."
                                    onChange={e => setModalityLanguage(label, e.target.value)}
                                    style={{
                                      width: "100%", fontSize: 11.5, padding: "6px 9px", borderRadius: 5,
                                      lineHeight: 1.5, border: "1px solid var(--border)",
                                      background: "var(--white)", color: "var(--text-secondary)",
                                      resize: "vertical",
                                    }} />
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {selected === PANE_PROCEDURES && (() => {
              const all = config.procedureLanguage || [];
              const q = overrideSearch.trim().toLowerCase();
              const shown = q ? all.filter(p => (p.label || "").toLowerCase().includes(q)) : all;
              const hasText = p => (p.examples || []).some(e => (e.body || "").trim());
              const written = procedureWritten;
              return (
                <>
                  <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 3 }}>Procedure language</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 10 }}>
                    Example letters for cases with that OR Procedure. One procedure can cover
                    operations that read differently, so add as many as it needs: a posterior lumbar
                    case may be a discectomy with no hardware or a fusion with instrumentation. Each
                    carries a label, and the model follows the one whose label matches the case. It
                    takes the shape and the phrasing and no clinical facts, so a finding the case does
                    not have is dropped rather than carried across. Mark slots with square brackets, as
                    the templates do: [procedure], [region], [his/her]. A procedure with nothing
                    written contributes nothing and the shared paragraph stands on its own.
                  </div>
                  <div style={{
                    fontSize: 11.5, color: "var(--text-secondary)", background: "var(--gray-50, #f9fafb)",
                    border: "1px solid var(--border)", borderRadius: 7, padding: "8px 11px", marginBottom: 11,
                  }}>
                    {written} of {all.length} written, marked with a filled dot. The level feeds the
                    [region] word in paragraph 2 and the deficit list in paragraph 4.
                  </div>
                  <input value={overrideSearch} onChange={e => setOverrideSearch(e.target.value)}
                    placeholder="Filter procedures..." style={{ ...inputStyle, marginBottom: 11 }} />

                  {shown.map(p => {
                    const open = openOverride === p.key;
                    const has = hasText(p);
                    return (
                      <div key={p.key} style={{ border: "1px solid var(--border)", borderRadius: 7, marginBottom: 8, overflow: "hidden" }}>
                        <button onClick={() => setOpenOverride(open ? "" : p.key)} style={{
                          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "9px 12px", background: open ? "var(--gray-50, #f9fafb)" : "var(--white)",
                          border: "none", cursor: "pointer", color: "var(--text-primary)",
                          textAlign: "left", gap: 10,
                        }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, minWidth: 0 }}>
                            {/* A dot rather than a tag on every row: most of these are
                                empty to begin with, and 34 labels saying so is noise. */}
                            <span title={has ? "Has language" : "Nothing written yet"}
                              style={{
                                width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                                background: has ? "var(--blue-500, #2563eb)" : "var(--gray-300, #d1d5db)",
                              }} />
                            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{p.label}</span>
                          </span>
                          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <Tag kind="quiet">{p.level || "no level"} &middot; {p.cases || 0} cases</Tag>
                            <ChevronIcon open={open} />
                          </span>
                        </button>
                        {open && (
                          <div style={{ padding: "0 12px 12px" }}>
                            <label style={{ display: "block", margin: "10px 0" }}>
                              <div style={fieldLabel}>Anatomical level</div>
                              <input value={p.level || ""} onChange={e => setProcedureLevel(p.key, e.target.value)}
                                placeholder="cervical, lumbar, thoracolumbar, cranial, thyroid_ent..."
                                style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} />
                            </label>
                            {(() => {
                              const exs = p.examples || [];
                              if (exs.length === 0) {
                                // The add control lives in the tab bar, which is not
                                // rendered without tabs, so the empty state carries
                                // its own or the first example can never be written.
                                return (
                                  <div style={{ padding: "10px 0 4px" }}>
                                    <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 9 }}>
                                      No examples yet. Cases with this procedure use the shared paragraph.
                                    </div>
                                    <button className="btn btn-secondary btn-sm"
                                      onClick={() => addExample(p.key)} style={{ fontSize: 11 }}>
                                      + Add example
                                    </button>
                                  </div>
                                );
                              }
                              const activeKey = exs.some(e => e.key === openExample[p.key])
                                ? openExample[p.key] : exs[0].key;
                              const ex = exs.find(e => e.key === activeKey);
                              return (
                                <>
                                  <div style={{
                                    display: "flex", gap: 2, alignItems: "flex-end", marginBottom: -1,
                                    borderBottom: "1px solid var(--border)", flexWrap: "wrap",
                                  }}>
                                    {exs.map((e, i) => {
                                      const on = e.key === activeKey;
                                      const empty = !(e.body || "").trim();
                                      return (
                                        <button key={e.key}
                                          onClick={() => setOpenExample(m => ({ ...m, [p.key]: e.key }))}
                                          style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "6px 11px", fontSize: 11.5, cursor: "pointer",
                                            border: "1px solid var(--border)",
                                            borderBottom: on ? "1px solid var(--white)" : "1px solid var(--border)",
                                            borderRadius: "6px 6px 0 0", marginBottom: -1,
                                            background: on ? "var(--white)" : "var(--gray-50, #f9fafb)",
                                            color: on ? "var(--text-primary)" : "var(--text-secondary)",
                                            fontWeight: on ? 600 : 400, maxWidth: 210,
                                          }}>
                                          <span style={{
                                            width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                                            background: empty ? "var(--gray-300, #d1d5db)" : "var(--blue-500, #2563eb)",
                                          }} />
                                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {(e.label || "").trim() || `Example ${i + 1}`}
                                          </span>
                                        </button>
                                      );
                                    })}
                                    <button onClick={() => addExample(p.key)} title="Add another example"
                                      style={{
                                        padding: "6px 10px", fontSize: 13, cursor: "pointer", marginBottom: -1,
                                        border: "1px solid var(--border)", borderRadius: "6px 6px 0 0",
                                        background: "var(--gray-50, #f9fafb)", color: "var(--text-secondary)",
                                      }}>+</button>
                                  </div>

                                  <div style={{
                                    border: "1px solid var(--border)", borderTop: "none",
                                    borderRadius: "0 0 7px 7px", padding: "11px",
                                  }}>
                                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 8 }}>
                                      <label style={{ flex: 1 }}>
                                        <div style={fieldLabel}>
                                          Label &mdash; names the tab, and is how the model tells the examples apart
                                        </div>
                                        <input value={ex.label || ""}
                                          placeholder="Discectomy, no hardware"
                                          onChange={e => setExample(p.key, ex.key, "label", e.target.value)}
                                          style={inputStyle} />
                                      </label>
                                      <button className="btn btn-secondary btn-sm"
                                        onClick={() => removeExample(p.key, ex.key, ex.label)}
                                        style={{ fontSize: 11, color: "#dc2626" }}>Remove</button>
                                    </div>
                                    <textarea value={ex.body || ""} rows={14}
                                      placeholder="The example letter. Use [brackets] for anything that comes from the case."
                                      onChange={e => setExample(p.key, ex.key, "body", e.target.value)}
                                      style={{ ...inputStyle, lineHeight: 1.6, resize: "vertical" }} />
                                    {exs.length > 1 && (
                                      <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 7 }}>
                                        All {exs.length} are given to the model with their labels. It follows
                                        the one whose label matches the case.
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {shown.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "14px 0" }}>
                      Nothing matches that filter.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {showHistory && (
        <HistoryPanel userName={userName} onClose={() => setShowHistory(false)}
          onRolledBack={() => { setShowHistory(false); setJustSaved(false); load(true); }} />
      )}
      {showPreview && (
        <PreviewPanel config={config} dirty={dirty} onClose={() => setShowPreview(false)} />
      )}
    </>
  );
}

/* ─── styles ────────────────────────────────────────────────────── */
const iconBtn = disabled => ({
  border: "1px solid var(--border)", background: "var(--white)", borderRadius: 5, cursor: disabled ? "default" : "pointer",
  fontSize: 12, lineHeight: 1, padding: "3px 7px", color: disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
  opacity: disabled ? 0.45 : 1,
});

const fieldLabel = {
  fontSize: 10, fontFamily: "var(--font-mono)", textTransform: "uppercase",
  color: "var(--text-tertiary)", marginBottom: 4,
};

const inputStyle = {
  width: "100%", fontSize: 12, padding: "7px 10px", borderRadius: 6,
  border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-primary)",
};

const railHead = {
  fontSize: 9.5, fontFamily: "var(--font-mono)", letterSpacing: "0.08em", textTransform: "uppercase",
  color: "var(--text-tertiary)", padding: "9px 12px 7px", borderBottom: "1px solid var(--border)",
  background: "var(--gray-50, #f9fafb)", fontWeight: 600,
};

const railRow = on => ({
  // textAlign: a button centres its text by default, which only shows once a
  // label is long enough to wrap.
  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
  textAlign: "left",
  padding: on ? "9px 12px 9px 9px" : "9px 12px", fontSize: 12.5, cursor: "pointer",
  borderBottom: "1px solid var(--gray-100, #f3f4f6)", borderLeft: on ? "3px solid var(--blue-500, #2563eb)" : "none",
  background: on ? "var(--blue-50, #eff6ff)" : "var(--white)",
  color: "var(--text-primary)", fontWeight: on ? 600 : 400,
  borderTop: "none", borderRight: "none",
});

const overlay = {
  position: "fixed", inset: 0, background: "rgba(10,31,61,0.45)", display: "flex",
  alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
};

const modal = {
  background: "var(--white)", borderRadius: 10, width: "100%", maxHeight: "86vh",
  overflow: "hidden", display: "flex", flexDirection: "column",
  boxShadow: "0 10px 40px rgba(10,31,61,0.25)",
};

const modalHead = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  padding: "14px 16px", borderBottom: "1px solid var(--border)",
};

const closeBtn = {
  border: "none", background: "none", fontSize: 22, lineHeight: 1, cursor: "pointer",
  color: "var(--text-tertiary)", padding: 0,
};

const errBox = {
  fontSize: 12, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca",
  borderRadius: 7, padding: "9px 12px", marginBottom: 12,
};

const previewCol = {
  flex: 1, minWidth: 0, border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px",
};

const previewLabel = {
  fontSize: 9.5, fontFamily: "var(--font-mono)", letterSpacing: "0.08em", textTransform: "uppercase",
  color: "var(--text-tertiary)", fontWeight: 600, marginBottom: 7,
};

const letterStyle = {
  fontSize: 11.5, lineHeight: 1.65, color: "var(--text-secondary)", whiteSpace: "pre-wrap",
};
